import { useEffect, useMemo, useState } from 'react'

import { useQuantPreview, type PreviewMeta } from '@/app/hooks/useQuantPreview'
import { useQuantStrategyView } from '@/app/hooks/useQuantStrategyView'
import { feedHintLine, type QuantFeedState } from '@/app/lib/quant/feedView'
import { formatMissingDays, utcDate } from '@/app/lib/quant/previewView'
import { buildPreviewModel } from '@/app/lib/quant/previewModel'
import { priceDigits } from '@/app/lib/quant/chartToDraw'
import { barChoices, clampBars, defaultBars, defaultTf } from '@/app/lib/quant/previewOptions'
import { liveToPreviewState, previewModeFor, readPreviewView, writePreviewView } from '@/app/lib/quant/quantPreviewView'
import type { ViewRate } from '@/app/lib/quant/viewProtocol'
import type { ParamValue, PreviewSupport } from '@/app/lib/quant/types'
import { useSignalLog } from '@/app/lib/signalLog'
import type { ChartViewCommand, QuantViewChange } from '@/ui/QuantChart'
import {
  QuantPreviewPanel,
  type QuantPreviewBody,
  type QuantPreviewControls,
  type QuantPreviewSpan,
} from '@/ui/QuantPreviewPanel'

export interface QuantLivePreviewProps {
  slug: string
  /** The instrument the preview shows (one of `offered`). */
  instrument: string
  /** What the strategy's preview support offers (spec 039 6). */
  preview: PreviewSupport
  /** Instruments offered by both the preview and the form. */
  offered: string[]
  /** The "preview shows X; the form says Y" line, or `null`. */
  mismatch: string | null
  /** Spec 073: display label of an offered instrument; the value stays the bare name. */
  instrumentLabel?: (instrument: string) => string
  /** The operator picked an instrument; the pane also sets the form's `instrument`. */
  onInstrument: (instrument: string) => void
  /** Coerced values, or `null` while the form is invalid. */
  params: Record<string, ParamValue> | null
  /** The server's `invalid_params` text while it applies to the form as shown, else `null`. */
  onInvalidDetail?: (detail: string | null) => void
  /** The latest preview answer's meta (or `null`), lifted so the pane can offer "Use preview range". */
  onMeta?: (meta: PreviewMeta | null) => void
  /** "data through <date>" shown before a preview answer exists (the stored coverage), or `null`. */
  dataThroughFallback?: string | null
  /** The "Use preview range" button, or `null` when the strategy has no start/end params. */
  useRange?: { enabled: boolean; title: string; onUse: () => void } | null
  /** Spec 040: the shell's market feed state, for the "no live feed" hint. Absent = no hint. */
  feed?: QuantFeedState
}

/** A params update dims the overlays after this long; the progress strip shows after the second. */
export const PREVIEW_DIM_MS = 250
export const PREVIEW_STRIP_MS = 600

/**
 * The Config tab's animated preview (spec 036 D-5): wires `useQuantPreview` to
 * the presentational panel and owns the window/pan view state. It is keyed by
 * strategy in the parent, so switching strategy drops the stream.
 */
export function QuantLivePreview({
  slug,
  instrument,
  preview,
  offered,
  mismatch,
  onInstrument,
  instrumentLabel,
  params,
  onInvalidDetail,
  onMeta,
  dataThroughFallback = null,
  useRange = null,
  feed,
}: QuantLivePreviewProps) {
  const { emit } = useSignalLog()
  // Timeframe and bars: the stored choice if still valid, else the defaults. Written only from the handlers below.
  const [choice, setChoice] = useState<{ tf: string; bars: number }>(() => {
    const stored = readPreviewView(slug, preview)
    if (stored !== null) return stored
    const tf0 = defaultTf(null, preview)
    return { tf: tf0, bars: defaultBars(preview.max_bars[tf0] ?? 0) }
  })
  const { tf, bars } = choice
  function chooseTf(next: string) {
    const cap = preview.max_bars[next]
    if (cap === undefined) return
    const view = { tf: next, bars: clampBars(bars, cap) }
    setChoice(view)
    writePreviewView(slug, view)
  }
  function chooseBars(next: number) {
    const view = { tf, bars: next }
    setChoice(view)
    writePreviewView(slug, view)
  }
  // Spec 070: the live strategy view when the feed lists the instrument; otherwise (or after a refusal) the still REST preview.
  const [rate, setRate] = useState<ViewRate>(1)
  const feedKind = feed === undefined || feed.status === 'loading' ? 'loading' : feed.status === 'loaded' ? 'loaded' : 'unavailable'
  const liveInstruments = feed?.status === 'loaded' ? feed.feed.live_instruments : null
  const wantsLive = previewModeFor({ feed: feedKind, liveInstruments, instrument, fallbackCode: null }).kind === 'live'
  const liveView = useQuantStrategyView({ slug, instrument, tf, bars, params, rate, enabled: wantsLive })
  const mode = previewModeFor({ feed: feedKind, liveInstruments, instrument, fallbackCode: liveView.fallbackCode })
  const live = mode.kind === 'live'
  const rest = useQuantPreview({
    slug,
    instrument,
    tf,
    bars,
    params,
    enabled: !live,
    onRecomputed: (info) =>
      emit(
        'save',
        `Preview ${info.slug} · ${info.instrument} ${info.tf} recomputed on ${info.count} candles.`,
      ),
  })
  const liveState = useMemo(() => liveToPreviewState(liveView.state), [liveView.state])
  const state = live ? { ...liveState, retry: liveView.reconnect } : rest
  // The chart owns the visible range; this component sends it commands and mirrors which button is pressed.
  const [pressed, setPressed] = useState<QuantPreviewSpan | null>(240)
  const [view, setView] = useState<{ seq: number; command: ChartViewCommand }>({
    seq: 0,
    command: { type: 'window', width: 240 },
  })
  // An instrument, timeframe or bars change drops the candles: return to the selected width at the newest bar.
  const viewKey = `${instrument}|${tf}|${bars}`
  const [viewFor, setViewFor] = useState(viewKey)
  if (viewFor !== viewKey) {
    setViewFor(viewKey)
    setView((v) => ({ seq: v.seq + 1, command: { type: 'reset', width: pressed ?? 240 } }))
  }
  function onViewChange(change: QuantViewChange) {
    const p = change.pressed
    setPressed(p === 120 || p === 240 || p === 600 || p === 'all' ? p : null)
  }

  const meta = state.meta
  const dataThroughDay = meta !== null ? utcDate(meta.data_through) : dataThroughFallback
  const controls: QuantPreviewControls = {
    instruments: offered,
    instrumentLabel,
    onInstrument,
    timeframes: preview.timeframes.filter((t) => barChoices(t, preview.max_bars) !== null),
    onTimeframe: chooseTf,
    barOptions: barChoices(tf, preview.max_bars) ?? [bars],
    bars,
    onBars: chooseBars,
    mismatch,
    dataThrough: dataThroughDay !== null ? `data through ${dataThroughDay}` : null,
    feedHint:
      !live && feed?.status === 'loaded'
        ? feedHintLine({
            instrument,
            feedState: feed.state,
            symbols: feed.feed.symbols,
            dataThrough: dataThroughDay,
          })
        : null,
    useRange,
    liveLabel: mode.kind === 'rest' ? mode.label : null,
    rate: live ? { value: rate, onChange: setRate } : null,
  }
  useEffect(() => {
    onMeta?.(meta)
  }, [meta, onMeta])
  const overlay = state.overlay
  const model = useMemo(
    () => buildPreviewModel(state.candles, overlay, priceDigits(state.candles), { legend: 'none' }),
    [state.candles, overlay],
  )

  // How long the pending request has run. The timers belong to one request
  // (`pendingSeq`); state is only set from their callbacks.
  const pending = state.status === 'loading' || state.status === 'updating'
  const [waited, setWaited] = useState({ seq: -1, dim: false, strip: false })
  useEffect(() => {
    if (!pending) return
    const seq = state.pendingSeq
    const dimTimer = setTimeout(
      () => setWaited((w) => ({ seq, dim: true, strip: w.seq === seq && w.strip })),
      PREVIEW_DIM_MS,
    )
    const stripTimer = setTimeout(
      () => setWaited({ seq, dim: true, strip: true }),
      PREVIEW_STRIP_MS,
    )
    return () => {
      clearTimeout(dimTimer)
      clearTimeout(stripTimer)
    }
  }, [pending, state.pendingSeq])
  const mine = waited.seq === state.pendingSeq
  const showStrip = pending && mine && waited.strip
  const held = state.candles.length > 0
  const failureKept =
    held &&
    (state.status === 'failed' || state.status === 'unavailable' || state.status === 'not_covered')
  const dim = (state.status === 'updating' && overlay !== null && mine && waited.dim) || failureKept

  const serverInvalid =
    state.status === 'invalid' && params !== null && state.error?.code === 'invalid_params'
  const invalidDetail = serverInvalid ? (state.error?.message ?? null) : null
  useEffect(() => {
    onInvalidDetail?.(invalidDetail)
    return () => onInvalidDetail?.(null)
  }, [invalidDetail, onInvalidDetail])

  let stats: Array<[string, string | number]> | null = null
  let statsNote = 'Overlays: waiting for the preview'
  if (overlay !== null) {
    if (overlay.stats.length > 0) stats = overlay.stats
    else statsNote = 'No detections in this window'
  } else if (state.status === 'updating') {
    statsNote = 'Backend: running the strategy…'
  } else if (state.status === 'invalid') {
    statsNote = 'Overlays: paused'
  }

  let body: QuantPreviewBody | null = null
  const err = state.error
  if (state.status === 'loading' && !held) body = { kind: 'loading', slow: showStrip }
  else if (state.status === 'invalid' && !held)
    body = { kind: 'invalid', text: 'Fix the highlighted params to resume the preview' }
  else if (state.status === 'not_covered' && err !== null)
    body = {
      kind: 'not_covered',
      message: err.message,
      missingLine: formatMissingDays(err.missingDays, err.missingCount),
    }
  else if (state.status === 'failed' && err !== null) body = { kind: 'failed', detail: err.message }
  else if (state.status === 'unavailable' && err !== null)
    body = { kind: 'unavailable', headline: err.message }

  return (
    <QuantPreviewPanel
      instrument={instrument}
      tf={tf}
      count={state.candles.length}
      status={state.status}
      model={model}
      pressed={pressed}
      onWindow={(next) =>
        setView((v) => ({ seq: v.seq + 1, command: { type: 'window', width: next } }))
      }
      view={view}
      onViewChange={onViewChange}
      overlaySeq={state.overlaySeq}
      candleFrames={state.candleFrames}
      stats={stats}
      statsNote={statsNote}
      body={body}
      onRetry={state.retry}
      showStrip={showStrip}
      dim={dim}
      legend={overlay?.legend ?? []}
      controls={controls}
    />
  )
}
